/**
 * Lead ATTRIBUTION rules — the webapp's copy.
 *
 * ╔══════════════════════════════════════════════════════════════════════════════════╗
 * ║  DUPLICATE. The other copy is `server/lib/leadAttributionRules.js`.               ║
 * ║  CHANGE ONE, CHANGE BOTH — and keep the behaviour identical, not merely similar.  ║
 * ╚══════════════════════════════════════════════════════════════════════════════════╝
 *
 * The two files are deliberately duplicated rather than shared. `leadFormRules.js` solves
 * the same problem with a generated mirror under `src/shared/` plus an alias in
 * `next.config.mjs`, and that machinery was judged not worth extending for this module:
 * this logic is ~60 lines of pure string handling with no dependencies, and the cost of a
 * build-time mirror, a `.d.ts`, two bundler aliases and a drift check is higher than the
 * cost of keeping two short files in step by hand.
 *
 * That trade only holds if the duplication is VISIBLE, so:
 *   - both files carry this banner naming the other,
 *   - the server copy is the one with unit tests
 *     (`server/lib/__tests__/leadAttributionRules.test.mjs`), and those tests are the
 *     specification — if you change behaviour here, make that suite say so first,
 *   - the parameter lists and the fallbacks below must match the server copy exactly.
 *     They are what decide what is STORED on the lead row; the server re-reads the same
 *     columns when it delivers. A drift here does not throw, it mis-reports: a lead
 *     recorded as organic and delivered as a campaign, visible only in CRM data.
 *
 * ─── What the rules are ─────────────────────────────────────────────────────────────
 * Which query parameters count as attribution, and what an absent one means, depend on
 * where the form's leads are delivered. The two CRMs disagree, and both are right for
 * their own reporting.
 *
 *   erp   The ERP requires a non-blank source and has always been fed the university's own
 *         source with `media`/`campaign` defaulted to "Direct". A blank there reads as
 *         missing data rather than a direct arrival, and the ERP's own payload builder
 *         re-derives "Direct" anyway — so writing anything else would only disagree with
 *         what is delivered. It reads the BARE `?source=/media=/campaign=` triplet, which
 *         is the vocabulary this product has always published for hand-built links.
 *   lsq   LeadSquared reports on the `utm_*` headers. A stand-in there is not a default,
 *         it is a FABRICATED attribution: an organic lead filed under the university's
 *         source is indistinguishable in LSQ from a real campaign and therefore impossible
 *         to notice from the data. It reads ONLY `utm_*` — honouring a bare `?media=cpc`
 *         would let any hand-built link inject a campaign into LeadSquared's reporting —
 *         and leaves "" when the URL carried none.
 *
 * ─── Not used by the lead-drop widgets ──────────────────────────────────────────────
 * No widget imports this. They keep their own pre-existing behaviour through the widget
 * branch of `buildMobileOtpLeadPayload`, which is deliberately untouched by this module.
 */

/** The four columns these rules resolve. */
export interface ResolvedLeadAttribution {
  source_source: string;
  source_media: string;
  source_campaign: string;
  source_term: string;
}

/** Which rule set applies. `lsq_oms` drops like `lsq`, so it attributes like `lsq`. */
export type LeadAttributionFamily = "erp" | "lsq";

/** What `erp` writes when the URL said nothing. Never used by the `lsq` family. */
export const ERP_UNATTRIBUTED = "Direct";

export const ATTRIBUTION_FAMILY_ERP: LeadAttributionFamily = "erp";
export const ATTRIBUTION_FAMILY_LSQ: LeadAttributionFamily = "lsq";

/**
 * Destinations that deliver straight to LeadSquared.
 *
 * `lsq_oms` is here because it DROPS identically to `lsq` — the OMS learner account is a
 * separate step taken after delivery, not a different CRM. A destination that drops like
 * LSQ must attribute like LSQ, or the same payload reports two different origins depending
 * on which of the two an author picked.
 */
const LSQ_DESTINATIONS = ["lsq", "lsq_oms"];

/**
 * Which URL parameters each family reads, per column, in precedence order.
 *
 * MUST match `LEAD_ATTRIBUTION_PARAM_KEYS` in the server copy.
 */
export const LEAD_ATTRIBUTION_PARAM_KEYS: Readonly<
  Record<
    LeadAttributionFamily,
    Readonly<Record<keyof ResolvedLeadAttribution, readonly string[]>>
  >
> = {
  lsq: {
    source_source: ["utm_source"],
    source_media: ["utm_medium"],
    source_campaign: ["utm_campaign"],
    source_term: ["utm_term"],
  },
  erp: {
    source_source: ["source"],
    source_media: ["media"],
    source_campaign: ["campaign"],
    source_term: ["term"],
  },
};

const trimOrEmpty = (value: unknown): string =>
  value == null ? "" : String(value).trim();

/**
 * Which rules apply to a stored destination.
 *
 * Anything unknown — null (not chosen), a value from a newer build, a truncated write —
 * resolves to `erp`, because `erp` is what `resolveDropDestination` actually delivers such
 * a form to. Guessing `lsq` here would attribute a lead by rules its drop never uses.
 */
export function leadAttributionFamily(
  destination: string | null | undefined,
): LeadAttributionFamily {
  const id = trimOrEmpty(destination).toLowerCase();
  return LSQ_DESTINATIONS.includes(id)
    ? ATTRIBUTION_FAMILY_LSQ
    : ATTRIBUTION_FAMILY_ERP;
}

/** First non-empty value among `keys`, else "". */
function firstPresent(
  params: Record<string, string | null | undefined> | null | undefined,
  keys: readonly string[],
): string {
  if (!params || typeof params !== "object") return "";
  for (const key of keys) {
    const value = trimOrEmpty(params[key]);
    if (value) return value;
  }
  return "";
}

/**
 * Did the URL actually CARRY attribution for this destination?
 *
 * Distinct from "what did the rules resolve to", and the distinction is the whole reason
 * this exists. For `erp` a clean URL still resolves to the university source and
 * "Direct"/"Direct" — real values, but DEFAULTS rather than anything the visitor brought.
 * A returning visitor arriving organically would therefore look like they carried
 * attribution, and overwriting a stored campaign with those defaults would silently erase
 * the paid attribution the first visit recorded.
 *
 * So: true only when at least one of THIS family's parameters is present and non-empty on
 * the URL. Callers use it to decide whether a later visit may replace the stored tuple.
 */
export function hasUrlAttribution(
  destination: string | null | undefined,
  params: Record<string, string | null | undefined> | null | undefined,
): boolean {
  const keys = LEAD_ATTRIBUTION_PARAM_KEYS[leadAttributionFamily(destination)];
  return Object.values(keys).some((ks) => firstPresent(params, ks) !== "");
}

export interface ResolveLeadAttributionArgs {
  /** The form's stored `lead_destination`. */
  destination?: string | null;
  /** Flat `key → value` map of the query string, from `parseAttributionParams`. */
  params?: Record<string, string | null | undefined> | null;
  /** `university.source`; used by `erp` only, and NEVER by `lsq` — that is the point. */
  universitySource?: string | null;
}

/**
 * Resolve the four attribution columns for one visit.
 *
 * Always returns all four keys as strings, never undefined: "" is how an absent value is
 * spelled everywhere in this family (`data?.source_source || ""` server-side), and one
 * spelling is what stops "no campaign" from meaning three different things depending on
 * which code path wrote the row.
 */
export function resolveLeadAttribution({
  destination,
  params,
  universitySource,
}: ResolveLeadAttributionArgs = {}): ResolvedLeadAttribution {
  const family = leadAttributionFamily(destination);
  const keys = LEAD_ATTRIBUTION_PARAM_KEYS[family];
  const read = (column: keyof ResolvedLeadAttribution) =>
    firstPresent(params, keys[column]);

  if (family === ATTRIBUTION_FAMILY_LSQ) {
    /*
     * STRICT. No university fallback and no "Direct" — an absent parameter means the visit
     * carried no attribution, and that is exactly what LeadSquared should be told. The term
     * is treated the same way the vendor client already treats it (omitted rather than
     * invented), so all four columns now agree on that principle.
     */
    return {
      source_source: read("source_source"),
      source_media: read("source_media"),
      source_campaign: read("source_campaign"),
      source_term: read("source_term"),
    };
  }

  return {
    // The university's own source is the ERP's answer to "organic traffic on our property".
    source_source: read("source_source") || trimOrEmpty(universitySource),
    source_media: read("source_media") || ERP_UNATTRIBUTED,
    source_campaign: read("source_campaign") || ERP_UNATTRIBUTED,
    // No stand-in: a term names the one keyword a click arrived on, so inventing one would
    // be a fabricated attribution rather than a default. Matches the vendor client.
    source_term: read("source_term"),
  };
}

/**
 * Parse a query string into the flat map `resolveLeadAttribution` expects.
 *
 * Hand-rolled rather than `URLSearchParams` so this file and the server copy parse
 * IDENTICALLY — case folding and the first-occurrence rule especially. Accepts a full URL,
 * a bare search string, with or without the leading "?", and returns {} for anything
 * unparseable: a malformed URL must never stop a form rendering or a lead being captured.
 */
export function parseAttributionParams(
  search: string | null | undefined,
): Record<string, string> {
  const out: Record<string, string> = {};
  const raw = trimOrEmpty(search);
  if (!raw) return out;
  const query = raw.includes("?") ? raw.slice(raw.indexOf("?") + 1) : raw;
  if (!query) return out;
  for (const pair of query.split("&")) {
    if (!pair) continue;
    const eq = pair.indexOf("=");
    const rawKey = eq === -1 ? pair : pair.slice(0, eq);
    const rawValue = eq === -1 ? "" : pair.slice(eq + 1);
    let key = "";
    let value = "";
    try {
      key = decodeURIComponent(rawKey.replace(/\+/g, " ")).trim().toLowerCase();
      value = decodeURIComponent(rawValue.replace(/\+/g, " ")).trim();
    } catch {
      // A stray "%" makes decodeURIComponent throw. Fall back to the raw text rather than
      // dropping a parameter that is very likely still readable.
      key = rawKey.trim().toLowerCase();
      value = rawValue.trim();
    }
    // FIRST occurrence wins, matching `URLSearchParams.get`, so `?utm_source=a&utm_source=b`
    // resolves the same way on both sides.
    if (key && !(key in out)) out[key] = value;
  }
  return out;
}
